import { useMemo, useState, useEffect } from 'react';
import { useTasks } from './useTasks';
import { useProjects } from './useProjects';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';

export interface DateRange {
  from: Date;
  to: Date;
}

export interface ProjectStats {
  totalProjects: number;
  activeProjects: number;
  completedProjects: number;
  totalTasks: number;
  completedTasks: number;
  totalTime: number;
  billableTime: number;
  completionRate: number;
  overdueTasks: number;
}

export interface TasksByStatus {
  todo: number;
  in_progress: number;
  completed: number;
  cancelled: number;
}

export interface TasksByPriority {
  low: number;
  medium: number;
  high: number;
  urgent: number;
}

export const useProjectReports = (dateRange?: DateRange, userId?: string) => {
  const { tasks } = useTasks();
  const { projects } = useProjects();
  const { user } = useAuth();

  // Időbejegyzések betöltése a pontos óraszámításhoz
  const [timeEntriesData, setTimeEntriesData] = useState<{
    entries: any[];
    totalTime: number;
    billableTime: number;
  }>({ entries: [], totalTime: 0, billableTime: 0 });

  useEffect(() => {
    const fetchTimeData = async () => {
      if (!user?.organizationId) return;

      try {
        const q = query(
          collection(db, 'timeEntries'),
          where('organizationId', '==', user.organizationId)
        );
        const snapshot = await getDocs(q);
        const allEntries = snapshot.docs.map(doc => doc.data());

        let filteredEntries = allEntries;
        if (dateRange) {
          filteredEntries = allEntries.filter(entry => {
            const entryDate = toDate(entry.startTime);
            return entryDate >= dateRange.from && entryDate <= dateRange.to;
          });
        }
        if (userId) {
          filteredEntries = filteredEntries.filter(entry => entry.userId === userId);
        }

        const totalTime = filteredEntries.reduce((sum, e) => sum + (e.duration || 0), 0);
        const billableTime = filteredEntries
          .filter(e => e.isBillable)
          .reduce((sum, e) => sum + (e.duration || 0), 0);

        setTimeEntriesData({ entries: allEntries, totalTime, billableTime });
      } catch (error) {
        console.error('Error fetching time entries:', error);
      }
    };

    fetchTimeData();
  }, [user?.organizationId, dateRange, userId]);

  // Helper to convert Firestore timestamp to Date
  const toDate = (timestamp: any): Date => {
    if (!timestamp) return new Date();
    if (timestamp instanceof Date) return timestamp;
    if (typeof timestamp.toDate === 'function') return timestamp.toDate();
    return new Date(timestamp);
  };

  // Filter by date range and user if provided
  const filteredTasks = useMemo(() => {
    let filtered = tasks;
    
    // Filter by date range
    if (dateRange) {
      filtered = filtered.filter(task => {
        const createdAt = toDate(task.createdAt);
        return createdAt >= dateRange.from && createdAt <= dateRange.to;
      });
    }
    
    // Filter by user
    if (userId) {
      filtered = filtered.filter(task => task.assignedTo === userId);
    }
    
    return filtered;
  }, [tasks, dateRange, userId]);

  const filteredProjects = useMemo(() => {
    if (!dateRange) return projects;
    
    return projects.filter(project => {
      const createdAt = toDate(project.createdAt);
      return createdAt >= dateRange.from && createdAt <= dateRange.to;
    });
  }, [projects, dateRange]);

  // Project statistics
  const projectStats = useMemo((): ProjectStats => {
    const activeProjects = filteredProjects.filter(p => p.status === 'active').length;
    const completedProjects = filteredProjects.filter(p => p.status === 'completed').length;
    const completedTasks = filteredTasks.filter(t => t.status === 'completed').length;
    
    const completionRate = filteredTasks.length > 0 
      ? (completedTasks / filteredTasks.length) * 100 
      : 0;

    const now = new Date();
    const overdueTasks = filteredTasks.filter(task => {
      if (!task.dueDate || task.status === 'completed') return false;
      const dueDate = toDate(task.dueDate);
      return dueDate < now;
    }).length;

    return {
      totalProjects: filteredProjects.length,
      activeProjects,
      completedProjects,
      totalTasks: filteredTasks.length,
      completedTasks,
      totalTime: timeEntriesData.totalTime,
      billableTime: timeEntriesData.billableTime,
      completionRate,
      overdueTasks
    };
  }, [filteredProjects, filteredTasks, timeEntriesData]);

  // Tasks by status
  const tasksByStatus = useMemo((): TasksByStatus => {
    return {
      todo: filteredTasks.filter(t => t.status === 'todo').length,
      in_progress: filteredTasks.filter(t => t.status === 'in_progress').length,
      completed: filteredTasks.filter(t => t.status === 'completed').length,
      cancelled: filteredTasks.filter(t => t.status === 'cancelled').length
    };
  }, [filteredTasks]);

  // Tasks by priority
  const tasksByPriority = useMemo((): TasksByPriority => {
    return {
      low: filteredTasks.filter(t => t.priority === 'low').length,
      medium: filteredTasks.filter(t => t.priority === 'medium').length,
      high: filteredTasks.filter(t => t.priority === 'high').length,
      urgent: filteredTasks.filter(t => t.priority === 'urgent').length
    };
  }, [filteredTasks]);

  // Top projects by task count
  const topProjects = useMemo(() => {
    const projectTaskCounts = new Map<string, { name: string; count: number }>();
    
    filteredTasks.forEach(task => {
      if (task.projectId) {
        const project = filteredProjects.find(p => p.id === task.projectId);
        if (project) {
          const existing = projectTaskCounts.get(task.projectId) || { name: project.name, count: 0 };
          existing.count++;
          projectTaskCounts.set(task.projectId, existing);
        }
      }
    });

    return Array.from(projectTaskCounts.entries())
      .map(([id, data]) => ({ projectId: id, projectName: data.name, count: data.count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  }, [filteredTasks, filteredProjects]);

  // Get time tracking data
  const getTimeTrackingData = async () => {
    if (!user?.organizationId) return null;

    try {
      const timeEntriesQuery = query(
        collection(db, 'timeEntries'),
        where('organizationId', '==', user.organizationId)
      );
      const snapshot = await getDocs(timeEntriesQuery);
      const entries = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Filter by date range and user
      let filtered = entries;
      
      // Filter by date range
      if (dateRange) {
        filtered = filtered.filter(entry => {
          const startTime = toDate((entry as any).startTime);
          return startTime >= dateRange.from && startTime <= dateRange.to;
        });
      }
      
      // Filter by user
      if (userId) {
        filtered = filtered.filter(entry => (entry as any).userId === userId);
      }

      const totalTime = filtered.reduce((sum, entry) => sum + ((entry as any).duration || 0), 0);
      const billableTime = filtered
        .filter(entry => (entry as any).isBillable)
        .reduce((sum, entry) => sum + ((entry as any).duration || 0), 0);

      return {
        totalTime,
        billableTime,
        nonBillableTime: totalTime - billableTime,
        entryCount: filtered.length
      };
    } catch (error) {
      console.error('Error fetching time entries:', error);
      return null;
    }
  };

  // Format seconds to hours
  const formatHours = (seconds: number): string => {
    const hours = seconds / 3600;
    return hours.toFixed(2);
  };

  // Format seconds to HH:MM:SS
  const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Get completed tasks with project and user names
  const getCompletedTasks = () => {
    // Start with all completed tasks
    let completed = tasks.filter(task => task.status === 'completed');
    
    // Filter by date range (use completedDate for completed tasks)
    if (dateRange) {
      completed = completed.filter(task => {
        if (!task.completedDate) return false;
        const completedDate = toDate(task.completedDate);
        return completedDate >= dateRange.from && completedDate <= dateRange.to;
      });
    }
    
    // Filter by user
    if (userId) {
      completed = completed.filter(task => task.assignedTo === userId);
    }
    
    // Add project and user names, and calculate period-specific actual hours
    return completed
      .map(task => {
        const project = projects.find(p => p.id === task.projectId);

        // Pontos óraszámítás: időbejegyzések alapján a kiválasztott időszakra
        let periodActualHours = task.actualHours || 0;
        const taskEntries = timeEntriesData.entries.filter(e => e.taskId === task.id);
        if (taskEntries.length > 0 && dateRange) {
          const relevantSeconds = taskEntries
            .filter(e => {
              const entryDate = toDate(e.startTime);
              return entryDate >= dateRange.from && entryDate <= dateRange.to;
            })
            .reduce((sum, e) => sum + (e.duration || 0), 0);
          periodActualHours = relevantSeconds / 3600;
        }

        return {
          ...task,
          projectName: project?.name || '-',
          assignedToName: task.assignedToName || '-',
          actualHours: periodActualHours
        };
      })
      .sort((a, b) => {
        const dateA = a.completedDate ? new Date(a.completedDate).getTime() : 0;
        const dateB = b.completedDate ? new Date(b.completedDate).getTime() : 0;
        return dateB - dateA; // Newest first
      });
  };

  // Get all filtered tasks (not just completed)
  const getFilteredTasks = () => {
    return filteredTasks.map(task => {
      const project = projects.find(p => p.id === task.projectId);
      return {
        ...task,
        projectName: project?.name || '-'
      };
    });
  };

  // Get tasks with period-specific hours for billing reports
  // Includes tasks created in period + tasks with time entries in period
  const getTasksWithPeriodHours = () => {
    const taskIdsWithEntries = new Set<string>();
    const periodHoursByTask = new Map<string, number>();

    if (dateRange) {
      timeEntriesData.entries.forEach(entry => {
        const entryDate = toDate(entry.startTime);
        if (entryDate >= dateRange.from && entryDate <= dateRange.to) {
          if (userId && entry.userId !== userId) return;
          taskIdsWithEntries.add(entry.taskId);
          const duration = entry.duration || 0;
          periodHoursByTask.set(entry.taskId, (periodHoursByTask.get(entry.taskId) || 0) + duration / 3600);
        }
      });
    }

    const taskMap = new Map<string, any>();

    // Start with filtered tasks (created in period)
    filteredTasks.forEach(task => {
      taskMap.set(task.id, {
        ...task,
        projectName: projects.find(p => p.id === task.projectId)?.name || '-',
        periodActualHours: periodHoursByTask.get(task.id) || 0
      });
    });

    // Add tasks with time entries in period (even if created outside period)
    if (dateRange) {
      tasks.forEach(task => {
        if (taskIdsWithEntries.has(task.id) && !taskMap.has(task.id)) {
          taskMap.set(task.id, {
            ...task,
            projectName: projects.find(p => p.id === task.projectId)?.name || '-',
            periodActualHours: periodHoursByTask.get(task.id) || 0
          });
        }
      });
    }

    return Array.from(taskMap.values());
  };

  return {
    projectStats,
    tasksByStatus,
    tasksByPriority,
    topProjects,
    getTimeTrackingData,
    getCompletedTasks,
    getFilteredTasks,
    getTasksWithPeriodHours,
    formatHours,
    formatDuration
  };
};
